/**
 * <s-policy-toc> — Builds an "On this page" list from a policy's headings.
 *
 * Store policies mark their headings either as real h2/h3 elements or as a paragraph
 * whose whole text is bold (<p><strong>Heading</strong></p>). This element finds both
 * inside its target, gives each an id, marks bold-paragraph headings with
 * `.policy-heading` for styling, and renders links into [data-toc-list].
 * It hides itself when fewer than three headings are found.
 *
 * Data:
 *   data-target (on self) — CSS selector of the policy body to scan.
 * Required child markup:
 *   [data-toc-list] — <ol> that receives the links.
 */
class SPolicyToc extends HTMLElement {
  connectedCallback() {
    const body = document.querySelector(this.dataset.target || '');
    const list = this.querySelector('[data-toc-list]');
    if (!body || !list) return;

    const headings = [];
    for (const element of body.querySelectorAll('h2, h3, p')) {
      if (element.tagName === 'P') {
        const strong = element.querySelector(':scope > strong, :scope > b');
        const text = element.textContent.replace(/\s+/g, ' ').trim();
        if (!strong || !text || strong.textContent.replace(/\s+/g, ' ').trim() !== text) continue;
        // Short label-style lines only (e.g. "Email:" or long bold sentences are not section headings).
        if (text.endsWith(':') || text.length > 80) continue;
        element.classList.add('policy-heading');
      }
      headings.push(element);
    }

    if (headings.length < 3) {
      this.hidden = true;
      return;
    }

    const used = new Set();
    for (const heading of headings) {
      const label = heading.textContent.replace(/\s+/g, ' ').trim();
      let id = heading.id;
      if (!id) {
        const base = `policy-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'section'}`;
        id = base;
        let n = 2;
        while (used.has(id) || document.getElementById(id)) id = `${base}-${n++}`;
        heading.id = id;
      }
      used.add(id);

      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${id}`;
      link.textContent = label;
      link.className = 'policy-toc-link';
      item.append(link);
      list.append(item);
    }

    this.hidden = false;
  }
}

if (!customElements.get('s-policy-toc')) {
  customElements.define('s-policy-toc', SPolicyToc);
}
